/**
 * KRYBOT chat panel for MarketFit situation or product definition.
 * Allows users to chat with the agent to gather or refine situation/product data.
 * When the agent returns structured JSON, shows "Apply to form" to merge into the form.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Input, Button, List, Avatar, Typography, Space, Alert, Spin, Segmented, Modal, Radio } from 'antd';
import { RobotOutlined, UserOutlined, SendOutlined, CheckOutlined, CommentOutlined, FormOutlined } from '@ant-design/icons';
import { KrybotService } from '../../services/api/krybotService';
import { useUser } from '../../contexts/UserContext';
import type { ChatMessageResponse, ChatThreadResponse } from '../../types/krybot';

const { TextArea } = Input;
const { Paragraph } = Typography;

/** Detect if content looks like agent persona (instructions) - never show to user */
function looksLikePersona(content: string | undefined): boolean {
  if (!content || typeof content !== 'string') return false;
  const s = content.toLowerCase();
  return (
    s.includes('when ui_role is') ||
    s.includes('operate in structured modeling') ||
    s.includes('respect the current state_id') ||
    s.includes('do not suggest moving to the next state') ||
    s.includes('only return json when') ||
    s.includes('do not fabricate numeric scores') ||
    s.includes('do not give general startup advice') ||
    s.includes('if required_fields are missing') ||
    s.includes('you are a marketfit') ||
    s.includes('operate as a marketfit') ||
    s.includes('when ui_role') ||
    s.includes('required_fields') ||
    s.includes('guard_rules') ||
    s.includes('session_snapshot')
  );
}

/** Detect if assistant message is the greeting (duplicate of userPrompt) - hide when we show userPrompt */
function looksLikeGreeting(content: string | undefined, userPrompt: string | undefined): boolean {
  if (!content || !userPrompt?.trim()) return false;
  const c = content.trim().toLowerCase();
  const p = userPrompt.trim().toLowerCase();
  if (c === p) return true;
  if (c.startsWith(p) || p.startsWith(c)) return true;
  const greetingStarts = [
    'describe the situation.',
    'describe the product.',
    'what assets, needs, inputs, and outputs',
    'what inputs does it require and what benefits',
  ];
  return greetingStarts.some((g) => c.startsWith(g) && c.length < 120);
}

/** Try to extract JSON from markdown code blocks in the response */
function extractStructuredData(content: string): Record<string, unknown> | null {
  const jsonBlock = content.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonBlock) {
    try {
      return JSON.parse(jsonBlock[1].trim()) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  // Try parsing the whole content as JSON
  try {
    const parsed = JSON.parse(content.trim()) as Record<string, unknown>;
    if (typeof parsed === 'object' && parsed !== null) return parsed;
  } catch {
    // ignore
  }
  return null;
}

/** Compute structural diff: added, modified, removed paths */
function computeStructuralDiff(
  current: unknown,
  suggested: unknown
): { added: string[]; modified: string[]; removed: string[] } {
  const added: string[] = [];
  const modified: string[] = [];
  const removed: string[] = [];

  function collectPaths(obj: unknown, prefix: string): Set<string> {
    const paths = new Set<string>();
    if (obj === null || obj === undefined) return paths;
    if (Array.isArray(obj)) {
      obj.forEach((item, i) => {
        paths.add(`${prefix}[${i}]`);
        const sub = collectPaths(item, `${prefix}[${i}]`);
        sub.forEach((p) => paths.add(p));
      });
      return paths;
    }
    if (typeof obj === 'object') {
      const keys = Object.keys(obj as Record<string, unknown>);
      keys.forEach((k) => {
        const p = prefix ? `${prefix}.${k}` : k;
        paths.add(p);
        const sub = collectPaths((obj as Record<string, unknown>)[k], p);
        sub.forEach((sp) => paths.add(sp));
      });
    }
    return paths;
  }

  function diff(a: unknown, b: unknown, prefix: string): void {
    if (a === undefined && b !== undefined) {
      added.push(prefix || '(root)');
      return;
    }
    if (a !== undefined && b === undefined) {
      removed.push(prefix || '(root)');
      return;
    }
    if (typeof a !== typeof b || (Array.isArray(a) !== Array.isArray(b))) {
      modified.push(prefix || '(root)');
      return;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) modified.push(prefix || '(root)');
      const len = Math.max(a.length, b.length);
      for (let i = 0; i < len; i++) {
        diff(a[i], b[i], prefix ? `${prefix}[${i}]` : `[${i}]`);
      }
      return;
    }
    if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
      const aKeys = new Set(Object.keys(a as Record<string, unknown>));
      const bKeys = Object.keys(b as Record<string, unknown>);
      for (const k of bKeys) {
        const p = prefix ? `${prefix}.${k}` : k;
        if (!aKeys.has(k)) added.push(p);
        else diff((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k], p);
      }
      const aKeyList = Object.keys(a as Record<string, unknown>);
      for (const k of aKeyList) {
        if (!(k in (b as Record<string, unknown>))) removed.push(prefix ? `${prefix}.${k}` : k);
      }
      return;
    }
    if (a !== b) modified.push(prefix || '(root)');
  }

  diff(current, suggested, '');
  return { added, modified, removed };
}

/** Render diff summary and highlighted JSON block */
function DiffSummary({
  diff,
  suggestedValue,
}: {
  diff: { added: string[]; modified: string[]; removed: string[] };
  suggestedValue: unknown;
}) {
  const hasAny = diff.added.length > 0 || diff.modified.length > 0 || diff.removed.length > 0;
  if (!hasAny) return <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(suggestedValue, null, 2)}</div>;
  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: 11 }}>
        {diff.added.length > 0 && (
          <span style={{ marginRight: 12 }}>
            <span style={{ background: '#f6ffed', padding: '2px 6px', borderRadius: 2 }}>Added</span>: {diff.added.join(', ')}
          </span>
        )}
        {diff.modified.length > 0 && (
          <span style={{ marginRight: 12 }}>
            <span style={{ background: '#fffbe6', padding: '2px 6px', borderRadius: 2 }}>Modified</span>: {diff.modified.join(', ')}
          </span>
        )}
        {diff.removed.length > 0 && (
          <span>
            <span style={{ background: '#fff2f0', padding: '2px 6px', borderRadius: 2 }}>Removed</span>: {diff.removed.join(', ')}
          </span>
        )}
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(suggestedValue, null, 2)}</div>
    </div>
  );
}

/** Validate extracted data against expectedOutput schema (Phase 6). Returns sparse when valid but minimal. */
function validateExtracted(
  extracted: Record<string, unknown>,
  expectedOutput?: string,
  requiredFields?: string[]
): { valid: boolean; error?: string; sparse?: boolean } {
  const value = expectedOutput && extracted[expectedOutput] !== undefined
    ? extracted[expectedOutput]
    : extracted;
  if (value === undefined || value === null) {
    return { valid: false, error: 'No data to apply' };
  }
  if (typeof value === 'string') {
    if (!value.trim()) return { valid: false, error: 'Empty string value' };
    return { valid: true, sparse: value.trim().length < 20 };
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return { valid: false, error: 'Empty array' };
    return { valid: true, sparse: value.length < 2 };
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (requiredFields?.length) {
      const hasAny = requiredFields.some((k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== '');
      if (!hasAny) {
        return { valid: false, error: `Missing required field(s): ${requiredFields.join(', ')}` };
      }
    }
    if (Object.keys(obj).length === 0) return { valid: false, error: 'Empty object' };
    const sparse = requiredFields && Object.keys(obj).length < 3;
    return { valid: true, sparse };
  }
  return { valid: true };
}

export interface MarketFitFlowContext {
  sessionId: string;
  companyId: string;
}

export interface ApplyToFormOptions {
  merge?: boolean;
}

export interface MarketFitAgentChatProps {
  artifactType: 'situation' | 'product' | 'guided';
  artifactId: string | null;
  companyId: string;
  onApplyToForm?: (data: Record<string, unknown>, options?: ApplyToFormOptions) => void;
  /** For guided flow: session and company context */
  flowContext?: MarketFitFlowContext;
  /** State ID for guided flow (e.g. SituationBounded, ProductDefined) or definition pages */
  stateId?: string;
  /** Required fields for current state - persona can ask clarification if missing */
  requiredFields?: string[];
  /** Guard rules for current state - persona can vary behavior */
  guardRules?: string[];
  /** Session state snapshot for guided flow - persona has context */
  sessionSnapshot?: Record<string, unknown>;
  /** For guided flow Structure mode: expected output key for schema hint */
  expectedOutput?: string;
  /** User-facing question from frontend or local bridge config - never show Krybot persona */
  userPrompt?: string;
  /** Current form/session data to include with each message so the agent has context */
  formData?: Record<string, unknown> | object;
}

export type MarketFitChatMode = 'explore' | 'structure';

export const MarketFitAgentChat: React.FC<MarketFitAgentChatProps> = ({
  artifactType,
  artifactId,
  companyId,
  onApplyToForm,
  stateId,
  requiredFields,
  guardRules,
  sessionSnapshot,
  flowContext,
  expectedOutput,
  userPrompt,
  formData,
}) => {
  const { user } = useUser();
  const userId = user?.id;

  const [chatMode, setChatMode] = useState<MarketFitChatMode>('explore');
  const [thread, setThread] = useState<ChatThreadResponse | null>(null);
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastApplyableMessage, setLastApplyableMessage] = useState<{
    messageId: string;
    data: Record<string, unknown>;
    validationError?: string;
    sparse?: boolean;
  } | null>(null);
  const [diffPreviewVisible, setDiffPreviewVisible] = useState(false);
  const [applyMode, setApplyMode] = useState<'merge' | 'replace'>('replace');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const storageKey = `marketfit_thread_${artifactType}_${flowContext?.companyId ?? companyId}_${artifactId ?? flowContext?.sessionId ?? 'none'}`;

  const bootstrapThread = useCallback(async () => {
    const effectiveCompanyId = flowContext?.companyId ?? companyId;
    if (!userId || !effectiveCompanyId) return;
    setLoading(true);
    setError(null);
    try {
      const artifactContext: Record<string, unknown> =
        artifactType === 'guided' && flowContext
          ? {
              type: 'marketfit_guided',
              session_id: flowContext.sessionId,
              company_id: flowContext.companyId,
            }
          : {
              type: artifactType === 'situation' ? 'marketfit_situation' : 'marketfit_product',
              company_id: effectiveCompanyId,
            };
      if (artifactId && artifactType !== 'guided') {
        artifactContext[artifactType === 'situation' ? 'situation_id' : 'product_id'] = artifactId;
      }
      if (stateId) artifactContext.state_id = stateId;
      if (requiredFields?.length) artifactContext.required_fields = requiredFields;
      if (guardRules?.length) artifactContext.guard_rules = guardRules;
      if (sessionSnapshot && Object.keys(sessionSnapshot).length > 0) {
        artifactContext.session_snapshot = sessionSnapshot;
      }
      const storedThreadId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(storageKey) : null;
      console.debug('[MarketFitAgentChat] Bootstrap: storageKey=', storageKey, 'storedThreadId=', storedThreadId ?? '(none)');
      const marketFitAgent = await KrybotService.findMarketFitAgent();
      const response = await KrybotService.bootstrapThread({
        user_id: userId,
        thread_id: storedThreadId || undefined,
        artifact_context: artifactContext,
        ui_role: 'marketfit',
        context: {
          company_id: effectiveCompanyId,
        },
        preferred_agent_id: marketFitAgent?.id,
      });
      if (response.thread?.id && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(storageKey, response.thread.id);
      }
      setThread(response.thread);
      let raw = response.starter_messages || [];
      const reused = !!storedThreadId;
      console.debug(
        '[MarketFitAgentChat] Bootstrap returned',
        raw.length,
        'messages for thread',
        response.thread?.id,
        '(reused=',
        reused,
        ')'
      );
      // Fallback: when reusing a thread, bootstrap may return only the greeting (backend message_count stale).
      // Fetch full history to restore chat.
      if (reused && raw.length <= 1 && response.thread?.id) {
        try {
          const history = await KrybotService.getChatHistory(response.thread.id);
          if (history.messages && history.messages.length > raw.length) {
            raw = history.messages;
            console.debug('[MarketFitAgentChat] Fetched history: restored', raw.length, 'messages');
          }
        } catch {
          // Ignore; use bootstrap messages
        }
      }
      const filtered = raw.filter((m) => {
        if (m.role !== 'assistant') return true;
        if (looksLikePersona(m.content)) return false;
        if (userPrompt?.trim() && looksLikeGreeting(m.content, userPrompt)) return false;
        return true;
      });
      setMessages(filtered);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start chat';
      setError(msg);
      setThread(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [userId, companyId, artifactId, artifactType, stateId, requiredFields, guardRules, sessionSnapshot, flowContext, userPrompt, storageKey]);

  useEffect(() => {
    bootstrapThread();
  }, [bootstrapThread]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || !thread || !userId || sending) return;

    setInput('');
    setSending(true);
    setLastApplyableMessage(null);

    const userMessage: ChatMessageResponse = {
      id: `temp-${Date.now()}`,
      thread_id: thread.id,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    const hasFormData =
      formData &&
      typeof formData === 'object' &&
      Object.keys(formData).length > 0 &&
      Object.values(formData).some(
        (v) =>
          (Array.isArray(v) && v.length > 0) ||
          (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v as object).length > 0) ||
          (typeof v === 'string' && (v as string).trim().length > 0) ||
          (typeof v === 'number' && !Number.isNaN(v))
      );
    const structureHint =
      chatMode === 'structure' && requiredFields?.length
        ? `[Respond with JSON in a \`\`\`json code block. Include keys: ${requiredFields.join(', ')}.]\n\n`
        : '';
    const conversationContext =
      messages.length > 0
        ? messages
            .slice(-6)
            .map((m) => {
              const role = m.role === 'user' ? 'User' : 'Assistant';
              const c = String(m.content ?? '').slice(0, 600);
              return `${role}: ${c}${c.length >= 600 ? '...' : ''}`;
            })
            .join('\n\n')
        : '';
    const contextBlock =
      conversationContext.length > 0
        ? `---\nPrevious conversation:\n${conversationContext}\n---\n\n`
        : '';
    if (contextBlock.length > 0) {
      console.debug('[MarketFitAgentChat] Including conversation context in message:', contextBlock.length, 'chars');
    }
    const messageTextToSend = hasFormData
      ? `${structureHint}${contextBlock}${text}\n\n---\nCurrent form data for context:\n\`\`\`json\n${JSON.stringify(formData, null, 2)}\n\`\`\``
      : `${structureHint}${contextBlock}${text}`;

    const recentMessages = messages.slice(-10).map((m) => ({
      role: m.role,
      content: String(m.content ?? '').slice(0, 2000),
    }));

    try {
      const response = await KrybotService.sendMessage(thread.id, {
        thread_id: thread.id,
        user_id: userId,
        message_text: messageTextToSend,
        message_metadata: { chat_mode: chatMode, recent_messages: recentMessages },
      });

      if (response.thread) {
        setThread(response.thread);
        if (response.thread.id && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(storageKey, response.thread.id);
        }
      }

      if (response.assistant_message) {
        setMessages((prev) => [...prev, response.assistant_message!]);
        const content = response.assistant_message.content ?? '';
        console.debug('[MarketFitAgentChat] Agent full response:', {
          length: content.length,
          hasCodeBlock: /```/.test(content),
          preview: content.slice(0, 300) + (content.length > 300 ? '...' : ''),
        });
        const extracted = extractStructuredData(content);
        console.debug('[MarketFitAgentChat] extractStructuredData result:', extracted ? { keys: Object.keys(extracted) } : null);
        if (extracted && onApplyToForm) {
          const validation = validateExtracted(extracted, expectedOutput, requiredFields);
          console.debug('[MarketFitAgentChat] Validation:', { valid: validation.valid, sparse: validation.sparse, error: validation.error });
          if (validation.valid) {
            setLastApplyableMessage({
              messageId: response.assistant_message.id,
              data: extracted,
              sparse: validation.sparse,
            });
          } else {
            setLastApplyableMessage({
              messageId: response.assistant_message.id,
              data: extracted,
              validationError: validation.error,
            });
          }
        } else if (!extracted) {
          console.debug('[MarketFitAgentChat] No JSON found - agent returned prose, not ```json ... ```. Full content:', content);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send message';
      setError(msg);
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          thread_id: thread.id,
          role: 'assistant',
          content: `Error: ${msg}`,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, thread, userId, sending, onApplyToForm, expectedOutput, requiredFields, formData, storageKey, chatMode, messages]);

  const handleApply = useCallback(() => {
    if (lastApplyableMessage && onApplyToForm) {
      onApplyToForm(lastApplyableMessage.data, { merge: applyMode === 'merge' });
      setLastApplyableMessage(null);
      setDiffPreviewVisible(false);
    }
  }, [lastApplyableMessage, onApplyToForm, applyMode]);

  const suggestedValue =
    lastApplyableMessage && expectedOutput
      ? (lastApplyableMessage.data[expectedOutput] !== undefined
          ? lastApplyableMessage.data[expectedOutput]
          : lastApplyableMessage.data)
      : null;
  const currentValue =
    sessionSnapshot && expectedOutput ? sessionSnapshot[expectedOutput] : null;

  if (!userId) {
    return (
      <Card title="KRYBOT Agent">
        <Alert
          type="info"
          message="Sign in to use the Agent"
          description="The KRYBOT agent can help you define this situation or product through conversation. Sign in to get started."
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card title="KRYBOT Agent">
        <Spin spinning tip="Starting chat...">
          <div style={{ padding: 24, textAlign: 'center', minHeight: 80 }} />
        </Spin>
      </Card>
    );
  }

  if (error && !thread) {
    return (
      <Card title="KRYBOT Agent">
        <Alert
          type="warning"
          message="Could not start chat"
          description={error}
          action={
            <Button size="small" onClick={bootstrapThread}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>KRYBOT Agent</span>
          <Segmented
            size="small"
            value={chatMode}
            onChange={(v) => setChatMode(v as MarketFitChatMode)}
            options={[
              { value: 'explore', label: <span><CommentOutlined /> Explore</span> },
              { value: 'structure', label: <span><FormOutlined /> Structure</span> },
            ]}
          />
        </div>
      }
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 } }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          minHeight: 200,
          maxHeight: 400,
        }}
      >
        {(userPrompt?.trim() || messages.length > 0) && (
          <React.Fragment>
            <List
              dataSource={
              userPrompt?.trim()
                ? [
                    { id: '__user_prompt__', role: 'assistant' as const, content: userPrompt, thread_id: '', timestamp: '' },
                    ...messages,
                  ]
                : messages
            }
          renderItem={(msg) => {
            const extracted = msg.role === 'assistant' ? extractStructuredData(msg.content) : null;
            const isLastApplyable = lastApplyableMessage?.messageId === msg.id;
            const hasValidationError = isLastApplyable && lastApplyableMessage?.validationError;
            const isApplyable =
              extracted &&
              onApplyToForm &&
              isLastApplyable &&
              !hasValidationError;
            return (
              <List.Item
                style={{
                  border: 'none',
                  padding: '12px 0',
                  backgroundColor: msg.role === 'user' ? 'transparent' : '#fafafa',
                }}
              >
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar
                    icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    style={{
                      backgroundColor: msg.role === 'user' ? '#1890ff' : '#52c41a',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Paragraph style={{ marginTop: 0, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Paragraph>
                    {hasValidationError && (
                      <Alert
                        type="error"
                        message={lastApplyableMessage?.validationError}
                        style={{ marginTop: 8 }}
                        showIcon
                      />
                    )}
                    {isApplyable && lastApplyableMessage?.sparse && (
                      <Alert
                        type="warning"
                        message="Structured output minimal. You may want to refine before applying."
                        style={{ marginTop: 8 }}
                        showIcon
                      />
                    )}
                    {isApplyable && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => {
                          if (artifactType === 'guided' && sessionSnapshot && expectedOutput) {
                            setDiffPreviewVisible(true);
                          } else {
                            handleApply();
                          }
                        }}
                        style={{ marginTop: 8 }}
                      >
                        Apply to form
                      </Button>
                    )}
                  </div>
                </Space>
              </List.Item>
            );
          }}
            />
            <div ref={messagesEndRef} />
          </React.Fragment>
        )}
      </div>

      <div style={{ borderTop: '1px solid #e8e8e8', padding: 16, flexShrink: 0 }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              artifactType === 'guided'
                ? chatMode === 'structure'
                  ? 'Ask the agent to structure your input as JSON...'
                  : 'Refine your answer, ask clarification...'
                : chatMode === 'structure'
                  ? artifactType === 'situation'
                    ? 'Ask the agent to structure as JSON (assets, needs, inputs, outputs)...'
                    : 'Ask the agent to structure as JSON (inputs, benefits)...'
                  : artifactType === 'situation'
                    ? 'Describe the situation, refine ideas, ask clarification...'
                    : 'Describe the product, refine ideas, ask clarification...'
            }
            rows={3}
            disabled={sending || !thread}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={sending}
            disabled={!thread}
            style={{ height: 'auto' }}
          >
            Send
          </Button>
        </Space.Compact>
      </div>

      <Modal
        title="Apply to form"
        open={diffPreviewVisible}
        onOk={handleApply}
        onCancel={() => setDiffPreviewVisible(false)}
        okText="Apply"
        width={600}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {suggestedValue !== null && (
            <div>
              <Typography.Text strong>AI suggests:</Typography.Text>
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#f5f5f5',
                  borderRadius: 4,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                {currentValue !== undefined && currentValue !== null ? (
                  <DiffSummary
                    diff={computeStructuralDiff(currentValue, suggestedValue)}
                    suggestedValue={suggestedValue}
                  />
                ) : (
                  <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{JSON.stringify(suggestedValue, null, 2)}</div>
                )}
              </div>
            </div>
          )}
          {currentValue !== undefined && currentValue !== null && (
            <div>
              <Typography.Text strong>Current:</Typography.Text>
              <pre
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#fafafa',
                  borderRadius: 4,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                {JSON.stringify(currentValue, null, 2)}
              </pre>
            </div>
          )}
          {suggestedValue !== null && (
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Apply mode:</Typography.Text>
              <Radio.Group
                value={applyMode}
                onChange={(e) => setApplyMode(e.target.value)}
                options={[
                  { value: 'replace', label: 'Replace (overwrite current)' },
                  { value: 'merge', label: 'Merge (add/update, keep existing)' },
                ]}
              />
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default MarketFitAgentChat;
